import React, { useEffect, useMemo, useState } from 'react';
import { useEditor } from '../../context/EditorContext';
import { WpDialog, Field, CheckInput } from './DialogShell';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { FontPicker } from '../editor/FontPicker';
import { ArrowLeftRight, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { saveFontScheme, updateFontScheme, getCustomFontSchemes, BUILTIN_FONT_SCHEMES } from '../../lib/fontSchemes';
import { fontPairs } from '../../lib/fontPairs';
import { ensureFonts } from '../../lib/fontLoader';
import { apiErrorMessage } from '../../lib/projectApi';
import { useApplyFontScheme } from '../ribbon/FontSchemesMenu';

/**
 * Save — or edit in place — a heading + body pairing as a named font scheme (Publisher's "Create New Font Scheme").
 * Opened from Page Design ▸ Schemes ▸ Fonts ▸ Save Current Fonts as Scheme… (pre-filled with the fonts the publication uses),
 * from the font list's "Pairs well with" strip (selected font + its partner) and, with `props.editId`, from the pencil on a saved
 * scheme (rename or change its faces; the scheme keeps its id). Optionally (re-)applies the scheme right away.
 */
export const FontSchemeDialog = ({ props }) => {
  const { state, dispatch, closeDialog } = useEditor();
  const apply = useApplyFontScheme();
  const editing = props.editId ? getCustomFontSchemes().find((s) => s.id === props.editId) || null : null;
  const isEdit = !!props.editId;
  const isCurrent = isEdit && state.fontScheme?.id === props.editId; // the publication uses this very scheme
  const [heading, setHeading] = useState(editing?.heading || props.heading || 'Montserrat');
  const [body, setBody] = useState(editing?.body || props.body || fontPairs(props.heading || 'Montserrat', 1)[0] || 'Inter');
  const [name, setName] = useState(editing?.name || props.name || '');
  const [applyNow, setApplyNow] = useState(props.applyNow !== undefined ? !!props.applyNow : isEdit ? isCurrent : true);
  const [busy, setBusy] = useState(false);

  useEffect(() => { ensureFonts([heading, body]); }, [heading, body]);

  const suggested = useMemo(() => fontPairs(heading, 4).filter((f) => f.toLowerCase() !== body.toLowerCase()), [heading, body]);
  const trimmed = name.trim();
  const clash = trimmed && BUILTIN_FONT_SCHEMES.some((s) => s.name.toLowerCase() === trimmed.toLowerCase());
  const sameNameOther = trimmed && !clash && getCustomFontSchemes().some((s) => s.id !== props.editId && s.name.toLowerCase() === trimmed.toLowerCase());
  const overwrite = !isEdit && sameNameOther; // saving a new scheme under an existing name replaces that one
  const nameTaken = isEdit && sameNameOther; // editing must not swallow another scheme
  const unchanged = isEdit && editing && trimmed === editing.name && heading === editing.heading && body === editing.body;
  const fontsChanged = isEdit && editing && (heading !== editing.heading || body !== editing.body);

  const save = async () => {
    if (!trimmed || clash || nameTaken || busy) return;
    setBusy(true);
    try {
      if (isEdit) {
        const scheme = await updateFontScheme(props.editId, { name: trimmed, heading, body });
        toast.success(`Font scheme “${scheme.name}” updated`, { description: fontsChanged ? `${scheme.heading} for headings, ${scheme.body} for body text.` : 'Renamed.' });
        if (applyNow) apply(scheme);
        else if (isCurrent) dispatch({ type: 'SET_FONT_SCHEME', payload: { ...state.fontScheme, name: scheme.name } }); // keep the tick / tooltip in step; fonts stay as they are
      } else {
        const scheme = await saveFontScheme({ name: trimmed, heading, body });
        toast.success(`Font scheme “${scheme.name}” saved`, { description: 'It is listed under Page Design ▸ Schemes ▸ Fonts in every publication.' });
        if (applyNow) apply(scheme);
      }
      closeDialog();
    } catch (e) {
      toast.error(e?.message && !e.response ? e.message : await apiErrorMessage(e, 'The font scheme could not be saved.'));
    } finally { setBusy(false); }
  };

  if (isEdit && !editing) {
    return (
      <WpDialog name="font-scheme" title="Edit Font Scheme" width="w-[420px]" onCancel={closeDialog} cancelLabel="Close">
        <p className="text-sm text-muted-foreground" data-testid="font-scheme-missing">This font scheme no longer exists.</p>
      </WpDialog>
    );
  }

  const hint = clash ? 'That name belongs to a built-in scheme — choose another.' : nameTaken ? 'Another scheme of yours already has this name.' : overwrite ? 'A scheme of yours already has this name; it will be replaced.' : undefined;

  return (
    <WpDialog
      name="font-scheme"
      title={isEdit ? 'Edit Font Scheme' : 'Save Font Scheme'}
      description={isEdit ? 'Rename the scheme or change its heading / body fonts. It keeps its place in Page Design ▸ Schemes ▸ Fonts in every publication.' : 'A font scheme pairs a heading font with a body font. Saved schemes are offered in every publication and re-font the whole publication in one step.'}
      width="w-[560px]"
      onCancel={closeDialog}
      onOk={save}
      okLabel={busy ? 'Saving…' : isEdit ? 'Save Changes' : overwrite ? 'Replace Scheme' : 'Save Scheme'}
      okDisabled={!trimmed || !!clash || !!nameTaken || busy || (unchanged && !applyNow)}
    >
      <div className="space-y-4" data-testid="font-scheme-dialog" data-mode={isEdit ? 'edit' : 'create'}>
        <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
          <Field label="Heading font"><FontPicker value={heading} onChange={setHeading} testId="font-scheme-heading-picker" allowSaveScheme={false} /></Field>
          <Button type="button" variant="ghost" size="icon" className="h-8 w-8 mb-0.5" onClick={() => { setHeading(body); setBody(heading); }} title="Swap heading and body fonts" aria-label="Swap heading and body fonts" data-testid="font-scheme-swap"><ArrowLeftRight className="h-4 w-4" /></Button>
          <Field label="Body font"><FontPicker value={body} onChange={setBody} testId="font-scheme-body-picker" allowSaveScheme={false} /></Field>
        </div>

        {suggested.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="font-scheme-suggestions">
            <Sparkles className="h-3 w-3" /> Body fonts that pair with <span className="font-medium text-foreground">{heading}</span>:
            {suggested.map((f) => <button key={f} type="button" onClick={() => setBody(f)} className="rounded-full border border-border bg-background px-2 py-0.5 text-[12px] text-foreground hover:border-primary/60 hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" style={{ fontFamily: `"${f}"` }} data-testid={`font-scheme-suggest-${f.replace(/\s+/g, '-')}`}>{f}</button>)}
          </div>
        )}

        <div className="rounded-md border border-border bg-card p-4" data-testid="font-scheme-preview">
          <div className="text-[26px] leading-tight font-bold text-foreground" style={{ fontFamily: `"${heading}"` }}>Heading in {heading}</div>
          <div className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground" style={{ fontFamily: `"${body}"` }}>Body text set in {body}. The quick brown fox jumps over the lazy dog — 0123456789.</div>
        </div>

        <Field label="Scheme name" hint={hint}>
          <Input autoFocus value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') save(); }} placeholder={`${heading} + ${body}`} className={`h-8 bg-background border-border ${clash || nameTaken ? 'border-destructive' : ''}`} aria-invalid={!!clash || !!nameTaken} data-testid="font-scheme-name" />
        </Field>
        <CheckInput checked={applyNow} onChange={setApplyNow} label={isEdit ? (isCurrent ? 'Re-apply to this publication (it uses this scheme) — undoable' : 'Apply to this publication now (undoable)') : 'Apply to this publication now (undoable)'} testId="font-scheme-apply-now" />
      </div>
    </WpDialog>
  );
};
